// @systeme: auth-identite
// ============================================================================
// CECODEV Platform - Forgot Password Page
// ============================================================================

import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Stack,
  CircularProgress,
  Alert,
} from "@mui/material";
import { ArrowLeft } from "lucide-react";
import { api, messageApi } from "../lib/api";
import { toast } from "sonner";

export default function ForgotPasswordPage() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(() => searchParams.get("email") ?? "");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsLoading(true);

    try {
      await api.post("/auth/forgot-password", { email });
      setIsSuccess(true);
      toast.success("Email envoyé ! Vérifiez votre boîte de réception.");
    } catch (error: unknown) {
      toast.error(messageApi(error, "Erreur lors de l'envoi de l'email"));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Box className="auth-background">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          px: 2,
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: 480,
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
          }}
          className="fade-in"
        >
          <CardContent sx={{ p: 5 }}>
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box className="hex-icon-container" sx={{ mb: 2, justifyContent: "center" }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "white" }}>
                  C
                </Typography>
              </Box>
              <Typography
                variant="h3"
                className="gradient-text"
                sx={{ fontWeight: 700, mb: 1 }}
              >
                CECODEV
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                Mot de passe oublié
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Entrez votre email pour réinitialiser votre mot de passe
              </Typography>
            </Box>

            {isSuccess ? (
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Un email de réinitialisation a été envoyé à <strong>{email}</strong>.
                </Typography>
                <Typography variant="body2">
                  Vérifiez votre boîte de réception et suivez les instructions.
                </Typography>
              </Alert>
            ) : (
              <form onSubmit={handleSubmit}>
                <Stack spacing={3}>
                  <TextField
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    fullWidth
                    placeholder="votre.email@exemple.com"
                    autoComplete="email"
                    autoFocus
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                        Envoi...
                      </>
                    ) : (
                      "Envoyer le lien de réinitialisation"
                    )}
                  </Button>
                </Stack>
              </form>
            )}

            <Box sx={{ mt: 3, textAlign: "center" }}>
              <Link
                to="/login"
                style={{
                  color: "#4BB7A5",
                  textDecoration: "none",
                  fontWeight: 600,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <ArrowLeft size={16} />
                Retour à la connexion
              </Link>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}


